import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { resolveRedirect, type RedirectRule } from "@/lib/redirectMatch";

export default function RedirectsResolver() {
  const location = useLocation();
  const navigate = useNavigate();

  const { data: redirects } = useQuery({
    queryKey: ["redirects-active"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("redirects")
        .select("from_path,to_path")
        .eq("active", true);
      if (error) throw error;
      return (data || []) as RedirectRule[];
    },
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (!redirects || redirects.length === 0) return;
    // Don't redirect inside the admin panel
    if (location.pathname.startsWith("/admin")) return;

    const result = resolveRedirect(redirects, location.pathname, location.search, location.hash);
    if (!result) return;

    // Fire-and-forget hit counter — keyed on the rule's stored from_path (with `*` intact)
    supabase.rpc("increment_redirect_hit", { _from_path: result.rule.from_path }).then(() => {});

    if (result.external) {
      window.location.replace(result.target);
    } else {
      navigate(result.target, { replace: true });
    }
  }, [redirects, location.pathname, location.search, location.hash, navigate]);

  return null;
}
